import { useContext, useEffect, useState } from "react";
import { addStore, updateStore } from "../../../components/stores/functions";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";

const PROTECTED_STORE_ID = "651ea71ab7f1091f37f9230d";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha disabled:bg-monao";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";

export function buildStoreUpdateBody(fields, original) {
  const changes = {};
  if (fields.name !== original.name) changes.name = fields.name;
  if (fields.city !== original.city) changes.city = fields.city;
  if (fields.state !== original.state) changes.state = fields.state;
  if (fields.address !== original.address) changes.address = fields.address;
  if (fields.active !== original.active) changes.active = fields.active;
  if (fields.headquarter !== original.headquarter) changes.headquarter = fields.headquarter;
  return changes;
}

function StoreFormFields({ fields, setFields, nameDisabled = false, showHeadquarter = false }) {
  return (
    <>
      <MobileFormField label="Nome">
        <input
          type="text"
          aria-label="Nome"
          value={fields.name}
          onChange={(e) => setFields((current) => ({ ...current, name: e.target.value }))}
          disabled={nameDisabled}
          className={inputClass}
        />
      </MobileFormField>
      <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
        <input
          type="checkbox"
          checked={fields.active}
          onChange={() => setFields((current) => ({ ...current, active: !current.active }))}
          aria-label="Ativa"
        />
        Ativa
      </label>
      {showHeadquarter ? (
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
          <input
            type="checkbox"
            checked={fields.headquarter}
            onChange={() =>
              setFields((current) => ({ ...current, headquarter: !current.headquarter }))
            }
            aria-label="Headquarter"
          />
          Headquarter
        </label>
      ) : null}
      <MobileFormField label="Cidade">
        <input
          type="text"
          aria-label="Cidade"
          value={fields.city}
          onChange={(e) => setFields((current) => ({ ...current, city: e.target.value }))}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Estado">
        <input
          type="text"
          aria-label="Estado"
          value={fields.state}
          onChange={(e) => setFields((current) => ({ ...current, state: e.target.value }))}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Endereço">
        <input
          type="text"
          aria-label="Endereço"
          value={fields.address}
          onChange={(e) => setFields((current) => ({ ...current, address: e.target.value }))}
          className={inputClass}
        />
      </MobileFormField>
    </>
  );
}

export function MobileCreateStoreSheet({ open, onClose, user, onCreated }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [fields, setFields] = useState({
    name: "",
    city: "",
    state: "",
    address: "",
    active: false,
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFields({
      name: "",
      city: "",
      state: "",
      address: "",
      active: false,
    });
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (fields.name === "") {
      return badToast("Insira um nome");
    }
    if (fields.city === "") {
      return badToast("Insira uma cidade");
    }
    if (fields.state.length !== 2) {
      return badToast("Sigla de estado inválida");
    }
    if (fields.address === "") {
      return badToast("Insira um endereço");
    }

    setLoading(true);
    const data = await addStore(user.token, {
      name: fields.name,
      city: fields.city,
      state: fields.state,
      address: fields.address,
      active: fields.active,
    });
    setLoading(false);

    if (data.type === "success") {
      goodToast("Nova loja cadastrada!");
      resetForm();
      onCreated();
    } else {
      badToast("Erro ao cadastrar loja");
    }
  };

  if (!open) return null;

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Nova loja">
      <div className="space-y-4">
        <StoreFormFields fields={fields} setFields={setFields} />
        <div className="flex gap-3">
          <button type="button" onClick={handleClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={loading} className={primaryBtn}>
            Criar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

export function MobileStoreDetailSheet({ open, onClose, store, user, onChanged }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [fields, setFields] = useState({
    name: "",
    city: "",
    state: "",
    address: "",
    active: false,
    headquarter: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!store) return;
    setFields({
      name: store.name ?? "",
      city: store.city ?? "",
      state: store.state ?? "",
      address: store.address ?? "",
      active: Boolean(store.active),
      headquarter: Boolean(store.headquarter),
    });
  }, [store]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    const changes = buildStoreUpdateBody(fields, store);
    if (!Object.keys(changes).length) {
      badToast("Nenhuma alteração para salvar");
      return;
    }

    setLoading(true);
    const data = await updateStore(user.token, store._id, changes);
    setLoading(false);

    if (data.type === "success") {
      goodToast("Loja atualizada");
      onChanged();
    }
    if (data.type === "error") {
      badToast(data.message);
    }
  };

  if (!open || !store) return null;

  const nameDisabled = store._id?.toString() === PROTECTED_STORE_ID;

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Detalhe da loja">
      <div className="space-y-4">
        <MobileFormField label="ID">
          <input
            type="text"
            value={store._id}
            disabled
            className={inputClass}
            aria-label="ID"
          />
        </MobileFormField>
        <StoreFormFields
          fields={fields}
          setFields={setFields}
          nameDisabled={nameDisabled}
          showHeadquarter
        />
        <div className="flex gap-3">
          <button type="button" onClick={handleClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={loading} className={primaryBtn}>
            Atualizar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}
